import React from 'react';
import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconPlus, IconTrash } from '@tabler/icons';

// "true"/"1"/"sim"/"yes" contam como marcado - o backend normaliza pra
// "true"/"false" ao salvar, mas planilhas de importação ou dados antigos
// podem trazer "Sim"/"Não" direto, então o checkbox precisa reconhecer os
// dois jeitos na hora de exibir.
const BOOLEAN_TRUE_VALUES = ['true', '1', 'sim', 'yes'];
const isBooleanTrue = (value) => BOOLEAN_TRUE_VALUES.includes(String(value || '').trim().toLowerCase());

// Tipo de `<TextField>` HTML pra cada tipo de COLUNA de uma 'Lista de
// Itens' - só usado dentro de `ListaItensInput`.
const COLUNA_INPUT_TYPE = { number: 'number', coin: 'number', date: 'date' };

// 'lista_itens' (só existe em `PropostaField`) - tabela incremental com
// colunas pré-configuradas (`field.options`, `[{name,title,type_field}, ...]`)
// e linhas que o próprio usuário vai adicionando com o botão "+" na hora
// de preencher a Proposta. `value`/`onChange` seguem o mesmo contrato de
// string dos outros tipos - por baixo é um JSON serializado
// (`[{"<nome da coluna>": "<valor>"}, ...]`), pra não precisar de nenhuma
// mudança na `PropostaFieldValue.value` (TextField comum) nem no shape
// `{name: value}` que o formulário de Proposta já manda pro backend.
const ListaItensInput = ({ field, value, onChange }) => {
    const colunas = field.options || [];

    let linhas = [];
    try {
        const parsed = value ? JSON.parse(value) : [];
        if (Array.isArray(parsed)) linhas = parsed;
    } catch (e) {
        linhas = [];
    }

    const atualizarLinhas = (novasLinhas) => onChange(JSON.stringify(novasLinhas));

    const addLinha = () => {
        const nova = {};
        colunas.forEach((c) => {
            nova[c.name] = '';
        });
        atualizarLinhas([...linhas, nova]);
    };

    const removeLinha = (index) => atualizarLinhas(linhas.filter((_, i) => i !== index));

    const atualizarCelula = (index, nomeColuna, novoValor) => {
        atualizarLinhas(linhas.map((linha, i) => (i === index ? { ...linha, [nomeColuna]: novoValor } : linha)));
    };

    if (colunas.length === 0) {
        return (
            <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    "{field.title}" ainda não tem colunas configuradas - edite o campo em Campos Customizados pra adicionar.
                </Typography>
            </Grid>
        );
    }

    return (
        <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {field.title}
            </Typography>

            {linhas.length > 0 && (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {colunas.map((c) => (
                                <TableCell key={c.name} sx={{ fontWeight: 700 }}>
                                    {c.title}
                                </TableCell>
                            ))}
                            <TableCell sx={{ width: 44 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {linhas.map((linha, index) => (
                            <TableRow key={index}>
                                {colunas.map((c) => (
                                    <TableCell key={c.name}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            type={COLUNA_INPUT_TYPE[c.type_field] || 'text'}
                                            value={linha[c.name] || ''}
                                            onChange={(e) => atualizarCelula(index, c.name, e.target.value)}
                                            InputLabelProps={c.type_field === 'date' ? { shrink: true } : undefined}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <IconButton size="small" onClick={() => removeLinha(index)}>
                                        <IconTrash size={16} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Button size="small" startIcon={<IconPlus size={16} />} onClick={addLinha} sx={{ mt: 1, textTransform: 'none' }}>
                Adicionar item
            </Button>
        </Grid>
    );
};

// Renderiza o input certo pra um campo customizado (Organização/Cliente) de
// acordo com o `type_field` da definição - "boolean" vira Checkbox, "date"
// vira input nativo de data, o resto é um TextField comum.
const CustomFieldInput = ({ field, value, onChange, sx }) => {
    if (field.type_field === 'lista_itens') {
        return <ListaItensInput field={field} value={value} onChange={onChange} />;
    }

    if (field.type_field === 'boolean' || field.type_field === 'signature_space') {
        return (
            <Grid item xs={12} sm={6}>
                <FormControlLabel
                    control={<Checkbox checked={isBooleanTrue(value)} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />}
                    label={field.title}
                />
            </Grid>
        );
    }

    if (field.type_field === 'date') {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    label={field.title}
                    type="date"
                    fullWidth
                    required={field.required}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={sx}
                />
            </Grid>
        );
    }

    if (field.type_field === 'time') {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    label={field.title}
                    type="time"
                    fullWidth
                    required={field.required}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={sx}
                />
            </Grid>
        );
    }

    if (field.type_field === 'select') {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    select
                    label={field.title}
                    fullWidth
                    required={field.required}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    sx={sx}
                >
                    {(field.options || []).map((opt) => (
                        <MenuItem key={opt} value={opt}>
                            {opt}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        );
    }

    return (
        <Grid item xs={12} sm={6}>
            <TextField
                label={field.title}
                fullWidth
                required={field.required}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                sx={sx}
            />
        </Grid>
    );
};

export default CustomFieldInput;
